import {
  antennaOffsetMs, antennaParts, broadcastDayKey, broadcastDayWindow, startOfBroadcastDay,
} from './broadcast-day.js'

/**
 * Changement d'heure d'une journée d'antenne, côté interface.
 *
 * La timeline se mesure en temps écoulé (`broadcast-day.ts`) : elle est juste,
 * mais muette. Le 25 octobre, « 02:00 » s'y lit deux fois sans rien dire de
 * plus, et le premier passage de l'heure rejouée reste vide puisque les
 * créneaux du modèle posés à 02:xx tombent sur le second (`atBroadcastTime`,
 * côté serveur). Ce module nomme ce qui se passe pour que la grille le montre :
 * l'instant de la bascule, les deux passages de l'heure rejouée, et l'étiquette
 * (« ÉTÉ » / « HIVER ») qui lève l'ambiguïté d'une heure d'horloge.
 *
 * Tout est déduit du fuseau d'antenne, rien n'est codé en dur : ni 01:00 UTC,
 * ni le dernier dimanche du mois.
 */

export type ClockChangeKind = 'BACKWARD' | 'FORWARD'
export type ClockSeason = 'ÉTÉ' | 'HIVER'

/** Intervalle en heures écoulées depuis minuit d'antenne, demi-ouvert. */
export interface OffsetRange {
  fromHours: number
  toHours: number
}

export interface ClockChange {
  /** BACKWARD : l'heure est rejouée (octobre). FORWARD : elle est sautée (mars). */
  kind: ClockChangeKind
  /** Instant réel de la bascule. */
  atMs: number
  /** Position de la bascule dans la journée, en heures depuis minuit. */
  atHours: number
  /** Durée réelle de la journée : 23 ou 25 heures. */
  dayHours: number
  before: { season: ClockSeason; utcOffsetHours: number; clock: string }
  after: { season: ClockSeason; utcOffsetHours: number; clock: string }
  /** Retour à l'heure d'hiver : l'heure diffusée une première fois, puis une seconde. */
  firstPass: OffsetRange | null
  secondPass: OffsetRange | null
}

const HOUR_MS = 3_600_000

function clockOf(ms: number): string {
  const parts = antennaParts(ms)
  return `${String(parts.hour).padStart(2, '0')}:${String(parts.minute).padStart(2, '0')}`
}

/** « 03:00 » : ce que montrerait à `ms` une horloge restée sur `offsetMs`. */
function wallClockWithOffset(ms: number, offsetMs: number): string {
  const wall = new Date(ms + offsetMs)
  return `${String(wall.getUTCHours()).padStart(2, '0')}:${String(wall.getUTCMinutes()).padStart(2, '0')}`
}

/** En Europe/Paris, le décalage le plus fort est l'heure d'été. */
function seasonOf(offsetMs: number, otherMs: number): ClockSeason {
  return offsetMs > otherMs ? 'ÉTÉ' : 'HIVER'
}

const cache = new Map<string, ClockChange | null>()

/** Changement d'heure de la journée `AAAA-MM-JJ`, ou `null` un jour ordinaire. */
export function clockChangeOf(dayKey: string): ClockChange | null {
  const key = broadcastDayKey(dayKey)
  if (cache.has(key)) return cache.get(key)!
  const result = computeClockChange(key)
  cache.set(key, result)
  return result
}

function computeClockChange(dayKey: string): ClockChange | null {
  const { from, to } = broadcastDayWindow(dayKey)
  const fromMs = from.getTime()
  const toMs = to.getTime()
  const beforeOffset = antennaOffsetMs(fromMs)
  const afterOffset = antennaOffsetMs(toMs - 1)
  if (beforeOffset === afterOffset) return null

  // Dichotomie à la seconde : le décalage vaut `beforeOffset` jusqu'à la
  // bascule exclue, `afterOffset` à partir d'elle.
  let low = Math.floor(fromMs / 1000)
  let high = Math.floor((toMs - 1) / 1000)
  while (high - low > 1) {
    const middle = low + Math.floor((high - low) / 2)
    if (antennaOffsetMs(middle * 1000) === beforeOffset) low = middle
    else high = middle
  }
  const atMs = high * 1000
  const shiftMs = afterOffset - beforeOffset
  const atHours = (atMs - startOfBroadcastDay(dayKey).getTime()) / HOUR_MS
  const kind: ClockChangeKind = shiftMs < 0 ? 'BACKWARD' : 'FORWARD'
  const repeatedHours = Math.abs(shiftMs) / HOUR_MS

  return {
    kind,
    atMs,
    atHours,
    dayHours: Math.round((toMs - fromMs) / HOUR_MS),
    before: {
      season: seasonOf(beforeOffset, afterOffset),
      utcOffsetHours: beforeOffset / HOUR_MS,
      // L'heure qu'afficherait l'horloge restée sur l'ancien décalage.
      clock: wallClockWithOffset(atMs, beforeOffset),
    },
    after: {
      season: seasonOf(afterOffset, beforeOffset),
      utcOffsetHours: afterOffset / HOUR_MS,
      clock: clockOf(atMs),
    },
    firstPass: kind === 'BACKWARD' ? { fromHours: atHours - repeatedHours, toHours: atHours } : null,
    secondPass: kind === 'BACKWARD' ? { fromHours: atHours, toHours: atHours + repeatedHours } : null,
  }
}

/**
 * Saison d'un instant quand son heure d'horloge est ambiguë — c'est-à-dire
 * quand il tombe dans l'heure rejouée. `null` partout ailleurs : un « ÉTÉ »
 * accolé à toutes les heures de juillet ne dirait rien.
 */
export function ambiguousSeasonAt(instant: string | Date | number): ClockSeason | null {
  const ms = typeof instant === 'number' ? instant : new Date(instant).getTime()
  const change = clockChangeOf(broadcastDayKey(new Date(ms)))
  if (!change || change.kind !== 'BACKWARD' || !change.firstPass || !change.secondPass) return null
  const dayStart = startOfBroadcastDay(broadcastDayKey(new Date(ms))).getTime()
  const hours = (ms - dayStart) / HOUR_MS
  if (hours >= change.firstPass.fromHours && hours < change.firstPass.toHours) return change.before.season
  if (hours >= change.secondPass.fromHours && hours < change.secondPass.toHours) return change.after.season
  return null
}

/** Saison d'une position de la timeline dans l'heure rejouée, sinon `null`. */
export function ambiguousSeasonAtOffset(change: ClockChange | null, hours: number): ClockSeason | null {
  if (!change?.firstPass || !change.secondPass) return null
  if (hours >= change.firstPass.fromHours && hours < change.firstPass.toHours) return change.before.season
  if (hours >= change.secondPass.fromHours && hours < change.secondPass.toHours) return change.after.season
  return null
}

/** « UTC+2 » */
export function utcOffsetLabel(hours: number): string {
  return `UTC${hours >= 0 ? '+' : '−'}${Math.abs(hours)}`
}

/** Phrase d'en-tête de la journée, une seule. */
export function clockChangeSentence(change: ClockChange): string {
  if (change.kind === 'BACKWARD') {
    return `Cette nuit, à ${change.before.clock} heure d’été, l’horloge revient à ${change.after.clock} : `
      + `la tranche ${change.after.clock} → ${change.before.clock} est diffusée deux fois.`
  }
  return `Cette nuit, à ${change.before.clock} heure d’hiver, l’horloge avance à ${change.after.clock} : `
    + `la tranche ${change.before.clock} → ${change.after.clock} n’existe pas.`
}

/** Titre court du changement (étiquette d'en-tête). */
export function clockChangeTitle(change: ClockChange): string {
  return change.kind === 'BACKWARD' ? 'Retour à l’heure d’hiver' : 'Passage à l’heure d’été'
}

export interface ClockChangeGap extends OffsetRange {
  durationSec: number
}

/**
 * Trous qui entament le PREMIER passage de l'heure rejouée. La géométrie ne
 * prouve pas leur origine : la note rappelle l'heure supplémentaire à couvrir,
 * sans certifier qu'un comblage automatique aura lieu.
 * Un trou ordinaire — fût-il dans le second passage — n'est pas concerné.
 */
export function clockChangeGaps(
  change: ClockChange | null,
  dayKey: string,
  items: ReadonlyArray<{ startTime: string; endTime: string; status?: string }>,
): ClockChangeGap[] {
  if (!change?.firstPass || !change.secondPass) return []
  const dayStart = startOfBroadcastDay(dayKey).getTime()
  const rangeFrom = dayStart + change.firstPass.fromHours * HOUR_MS
  const rangeTo = dayStart + change.firstPass.toHours * HOUR_MS
  const spans = items
    .filter(item => item.status !== 'SKIPPED')
    .map(item => ({ start: new Date(item.startTime).getTime(), end: new Date(item.endTime).getTime() }))
    .filter(span => span.end > span.start)
    .sort((left, right) => left.start - right.start)

  const gaps: ClockChangeGap[] = []
  let cursor = Number.NEGATIVE_INFINITY
  for (const span of spans) {
    if (Number.isFinite(cursor) && span.start > cursor) pushGap(cursor, span.start)
    cursor = Math.max(cursor, span.end)
  }
  return gaps

  function pushGap(startMs: number, endMs: number) {
    // Un trou qui n'entame pas le premier passage ne le concerne pas.
    if (endMs <= rangeFrom || startMs >= rangeTo) return
    gaps.push({
      fromHours: (startMs - dayStart) / HOUR_MS,
      toHours: (endMs - dayStart) / HOUR_MS,
      durationSec: Math.round((endMs - startMs) / 1000),
    })
  }
}

/** Classe CSS d'une saison, partagée par la timeline, les bandes et le bandeau. */
export const CLOCK_SEASON_CLASS: Record<ClockSeason, 'is-summer' | 'is-winter'> = {
  'ÉTÉ': 'is-summer',
  'HIVER': 'is-winter',
}

/** Texte du repère : « 03:00 ÉTÉ → 02:00 HIVER · l'heure est rejouée ». */
export function clockChangeMarkerText(change: ClockChange): string {
  const shift = `${change.before.clock} ${change.before.season} → ${change.after.clock} ${change.after.season}`
  return change.kind === 'BACKWARD' ? `${shift} · l’heure est rejouée` : `${shift} · l’heure est sautée`
}

export interface ClockHourTag {
  season: ClockSeason
  /** « UTC+2 » */
  utc: string
  pass: '1er passage' | '2e passage'
}

/**
 * Étiquette de la graduation qui ouvre chaque passage de l'heure rejouée — les
 * deux « 02:00 » du 25 octobre. `null` pour toute autre graduation.
 */
export function clockChangeHourTag(change: ClockChange | null, offsetHours: number): ClockHourTag | null {
  if (!change?.firstPass || !change.secondPass) return null
  if (offsetHours === change.firstPass.fromHours) {
    return { season: change.before.season, utc: utcOffsetLabel(change.before.utcOffsetHours), pass: '1er passage' }
  }
  if (offsetHours === change.secondPass.fromHours) {
    return { season: change.after.season, utc: utcOffsetLabel(change.after.utcOffsetHours), pass: '2e passage' }
  }
  return null
}
